'use client';

import clsx from 'clsx';
import { HighlightIcon } from '@/assets/icons';
import { PAGE_SECTION_BAND_ACCENT, PAGE_SECTION_BAND_ELEVATED, pageSectionShell } from '@/constants/pageSections';
import { Heading2 } from '@/components/section/headings';
import { HomeFaqPreviewSection, HomeLeadCtaSection } from '@/components/section';
import AppLink from '@/components/i18n/AppLink';
import {
  H3,
  P,
  Span,
  Strong,
  SURFACE_REVEAL,
  SURFACE_STATIC,
  surfaceCard,
  surfaceCardInteractive,
} from '@/components/ui';
import type { FAQItem } from '@/components/section';
import type { BlogPostCard, PricingPlanCard, SpecialHighlight, SpecialStat, SpecialStep } from './types';
import { useState } from 'react';

export function SpecialSectionHeading({
  id,
  title,
  subtitle,
}: {
  id?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="page-heading-wrap" id={id}>
      <Heading2
        title={title}
        subtitle={subtitle}
        titleClassName="text-prose"
        subtitleClassName="mt-3 text-prose-muted"
      />
    </div>
  );
}

function statsGridClass(count: number): string {
  return count <= 4 ? 'page-grid-4' : 'page-grid-metrics';
}

export function SpecialStatsRow({ stats }: { stats: readonly SpecialStat[] }) {
  return (
    <div className={clsx('page-content-grid', statsGridClass(stats.length))}>
      {stats.map((s) => (
        <article key={s.label} className={clsx('page-stat-tile', SURFACE_REVEAL)}>
          <Strong className="text-xl font-semibold tracking-tight text-prose tabular-nums xs:text-2xl">
            {s.value}
          </Strong>
          <Span className="text-xs font-medium text-prose xs:text-sm">{s.label}</Span>
          {s.hint ? <P className="m-0 text-xs text-prose-muted">{s.hint}</P> : null}
        </article>
      ))}
    </div>
  );
}

export function SpecialPricingPlans({ plans }: { plans: readonly PricingPlanCard[] }) {
  return (
    <div className="page-content-grid page-grid-3">
      {plans.map((plan) => (
        <article
          key={plan.name}
          className={clsx(
            surfaceCardInteractive('flex h-full flex-col'),
            plan.highlighted && 'ring-2 ring-[color-mix(in_srgb,var(--color-primary)_35%,transparent)]'
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            {plan.badge ? <Span className="page-chip">{plan.badge}</Span> : null}
            <Span className="text-xs font-medium uppercase tracking-wide text-prose-muted">
              {plan.priceLabel}
            </Span>
          </div>
          <H3 className="m-0 mt-3 text-lg font-semibold text-prose">{plan.name}</H3>
          <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{plan.description}</P>
          <Ul className="page-card-list mt-4 flex-1">
            {plan.features.map((feature) => (
              <Li key={feature} className="li">
                <Span className="span">{feature}</Span>
              </Li>
            ))}
          </Ul>
          <AppLink
            href={plan.ctaHref}
            className={clsx(
              'mt-6 inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold transition',
              plan.highlighted
                ? 'bg-[var(--color-primary)] text-white hover:opacity-90'
                : 'border border-[color-mix(in_srgb,var(--semantic-border-default)_50%,transparent)] bg-[var(--semantic-surface-elevated)] text-prose hover:border-[var(--color-primary)]'
            )}
          >
            {plan.ctaLabel}
          </AppLink>
        </article>
      ))}
    </div>
  );
}

export function SpecialHighlightGrid({ items }: { items: readonly SpecialHighlight[] }) {
  return (
    <div className="page-content-grid page-grid-3">
      {items.map((item, i) => (
        <article key={i} className={surfaceCard('text-start')}>
          {item.icon ? (
            <Span className="page-highlight-icon" aria-hidden>
              <HighlightIcon name={item.icon} />
            </Span>
          ) : null}
          <H3 className="m-0 mt-3 text-base font-semibold text-prose">{item.title}</H3>
          <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{item.text}</P>
        </article>
      ))}
    </div>
  );
}

export function SpecialStepsList({ steps }: { steps: readonly SpecialStep[] }) {
  return (
    <ol className="page-content-grid page-grid-4 list-none p-0">
      {steps.map((step, i) => (
        <li key={step.title} className={clsx(surfaceCard('relative flex flex-col'))}>
          <Span className="page-consult-step__num mb-3" aria-hidden>
            {i + 1}
          </Span>
          <H3 className="m-0 text-base font-semibold text-prose">{step.title}</H3>
          <P className="m-0 mt-2 flex-1 text-sm text-prose-muted">{step.text}</P>
        </li>
      ))}
    </ol>
  );
}

export function SpecialProseBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className={clsx(surfaceCard(), 'mx-auto max-w-4xl')}>
      <H3 className="m-0 text-lg font-semibold text-prose">{title}</H3>
      <P className="m-0 mt-3 text-sm leading-relaxed text-prose-muted md:text-base">{body}</P>
    </div>
  );
}

export function SpecialTagCloud({ tags }: { tags: readonly string[] }) {
  return (
    <ul className="page-chip-list justify-center">
      {tags.map((tag) => (
        <li key={tag}>
          <Span className="page-chip">{tag}</Span>
        </li>
      ))}
    </ul>
  );
}

export function SpecialBlogPostList({
  posts,
  readLabel = 'Read more →',
}: {
  posts: readonly BlogPostCard[];
  readLabel?: string;
}) {
  return (
    <div className="page-content-grid gap-6">
      {posts.map((post) => (
        <article key={post.title} className={surfaceCard('flex flex-col')}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-prose-muted">
            {post.category ? <Span className="page-chip">{post.category}</Span> : null}
            {post.date ? <time dateTime={post.date}>{new Date(post.date).toLocaleDateString()}</time> : null}
          </div>
            <H3 className="m-0 mt-2 text-base font-semibold text-prose">
              <AppLink href={post.href} className="hover:underline">
                {post.title}
              </AppLink>
            </H3>
            <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{post.text}</P>
            <AppLink
              href={post.href}
              className="mt-3 inline-block text-sm font-medium text-[var(--color-primary)] hover:underline"
            >
              {readLabel}
            </AppLink>
        </article>
      ))}
    </div>
  );
}

export function SpecialLinkCards({
  cards,
  learnMoreLabel = 'Learn more →',
}: {
  cards: readonly { title: string; text: string; href: string }[];
  learnMoreLabel?: string;
}) {
  return (
    <div className="page-content-grid page-grid-3">
      {cards.map((card) => (
        <article key={card.href} className={surfaceCardInteractive('flex flex-col')}>
          <H3 className="m-0 text-base font-semibold text-prose">
            <AppLink href={card.href} className="hover:underline">
              {card.title}
            </AppLink>
          </H3>
          <P className="m-0 mt-2 flex-1 text-sm text-prose-muted">{card.text}</P>
          <AppLink href={card.href} className="mt-4 text-sm font-medium text-[var(--color-primary)] hover:underline">
            {learnMoreLabel}
          </AppLink>
        </article>
      ))}
    </div>
  );
}

export function SpecialMarketingFaq({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle?: string;
  items: readonly FAQItem[];
}) {
  return (
    <HomeFaqPreviewSection
      title={title}
      subtitle={subtitle}
      items={items}
      className={PAGE_SECTION_BAND_ELEVATED}
    />
  );
}

export function SpecialChecklist({ items }: { items: readonly string[] }) {
  return (
    <div className={clsx(surfaceCard(), 'mx-auto max-w-2xl', SURFACE_STATIC)}>
      <Ul className="page-consult-checklist m-0 p-0">
        {items.map((item) => (
          <Li key={item} className="page-consult-checklist__item">
            <Span className="page-consult-checklist__mark" aria-hidden>
              ✓
            </Span>
            <Span className="text-sm text-prose-muted">{item}</Span>
          </Li>
        ))}
      </Ul>
    </div>
  );
}

function Ul({ className, children }: { className?: string; children: React.ReactNode }) {
  return <ul className={className}>{children}</ul>;
}

function Li({ className, children }: { className?: string; children: React.ReactNode }) {
  return <li className={className}>{children}</li>;
}

export function SpecialGlassFaq({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle?: string;
  items: readonly FAQItem[];
}) {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);

  return (
    <section
      className={pageSectionShell(PAGE_SECTION_BAND_ELEVATED)}
      aria-labelledby="special-faq"
    >
      <SpecialSectionHeading id="special-faq" title={title} subtitle={subtitle} />
      <div className="mx-auto max-w-3xl space-y-2">
        {items.map((item) => {
          const open = openId === item.id;
          return (
            <div key={item.id} className={clsx(surfaceCard(), 'overflow-hidden', SURFACE_REVEAL)}>
              <button
                type="button"
                className="faq-accordion-trigger flex w-full items-center justify-between gap-3 px-4 py-4 sm:px-5"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : item.id)}
              >
                <Span className="text-sm font-medium text-prose">{item.question}</Span>
                <Span className="text-prose-muted" aria-hidden>
                  {open ? '−' : '+'}
                </Span>
              </button>
              {open ? (
                <P className="m-0 mt-3 border-t border-[color-mix(in_srgb,var(--semantic-border-default)_30%,transparent)] pt-3 text-sm leading-relaxed text-prose-muted">
                  {item.answer}
                </P>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SpecialLeadCta({
  title,
  body,
  primaryLabel,
  secondaryLabel,
  secondaryHref = '/services',
}: {
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel: string;
  secondaryHref?: string;
}) {
  return (
    <HomeLeadCtaSection
      title={title}
      body={body}
      primaryHref="/consultation"
      primaryLabel={primaryLabel}
      secondaryHref={secondaryHref}
      secondaryLabel={secondaryLabel}
      className={clsx(PAGE_SECTION_BAND_ACCENT, '!min-h-0')}
    />
  );
}

export function CaseStudyCardGrid({
  title,
  subtitle,
  links,
  readLabel = 'Read case study →',
}: {
  title: string;
  subtitle?: string;
  links: readonly { title: string; href: string; excerpt: string }[];
  readLabel?: string;
}) {
  return (
    <section className={pageSectionShell(PAGE_SECTION_BAND_ELEVATED)}>
      <SpecialSectionHeading title={title} subtitle={subtitle} />
      <div className="page-content-grid page-grid-3">
        {links.map((link) => (
          <article
            key={link.href}
            className={surfaceCardInteractive('flex flex-col')}
          >
            <H3 className="m-0 text-lg font-semibold text-prose">
              <AppLink href={link.href}>{link.title}</AppLink>
            </H3>
            <P className="m-0 mt-2 flex-1 text-sm leading-relaxed text-prose-muted">{link.excerpt}</P>
            <AppLink
              href={link.href}
              className="mt-4 text-sm font-semibold text-[var(--color-primary)] hover:underline"
            >
              {readLabel}
            </AppLink>
          </article>
        ))}
      </div>
    </section>
  );
}
